/** @jsxImportSource @emotion/react */
import React from 'react'
import { css } from '@emotion/react'
import colors from '@sportninja/common/constants/appColors'

/**
 * Checkbox icon - unchecked state (outline only)
 */
export const CheckboxOutlineIcon: React.FC<{ size?: number }> = ({
  size = 24,
}) => (
  <svg
    width={size}
    height={size}
    viewBox='0 0 24 24'
    fill='none'
    xmlns='http://www.w3.org/2000/svg'
  >
    <path
      d='M19 5V19H5V5H19ZM19 3H5C3.9 3 3 3.9 3 5V19C3 20.1 3.9 21 5 21H19C20.1 21 21 20.1 21 19V5C21 3.9 20.1 3 19 3Z'
      fill={colors.NEUTRAL_50}
    />
  </svg>
)

/**
 * Checkbox icon - checked state (filled with checkmark)
 */
export const CheckboxCheckedIcon: React.FC<{ size?: number }> = ({
  size = 24,
}) => (
  <svg
    width={size}
    height={size}
    viewBox='0 0 24 24'
    fill='none'
    xmlns='http://www.w3.org/2000/svg'
  >
    <path
      d='M19 3H5C3.89 3 3 3.9 3 5V19C3 20.1 3.89 21 5 21H19C20.11 21 21 20.1 21 19V5C21 3.9 20.11 3 19 3ZM10 17L5 12L6.41 10.59L10 14.17L17.59 6.58L19 8L10 17Z'
      fill={colors.PRIMARY_300}
    />
  </svg>
)

/**
 * Checkbox icon - indeterminate state (dash in box)
 */
export const CheckboxIndeterminateIcon: React.FC<{ size?: number }> = ({
  size = 24,
}) => (
  <svg
    width={size}
    height={size}
    viewBox='0 0 24 24'
    fill='none'
    xmlns='http://www.w3.org/2000/svg'
  >
    <path
      d='M19 3H5C3.9 3 3 3.9 3 5V19C3 20.1 3.9 21 5 21H19C20.1 21 21 20.1 21 19V5C21 3.9 20.1 3 19 3ZM17 13H7V11H17V13Z'
      fill={colors.PRIMARY_300}
    />
  </svg>
)

/**
 * Checkbox icon - disabled state (grayed out outline)
 */
export const CheckboxDisabledIcon: React.FC<{ size?: number }> = ({
  size = 24,
}) => (
  <svg
    width={size}
    height={size}
    viewBox='0 0 24 24'
    fill='none'
    xmlns='http://www.w3.org/2000/svg'
  >
    <path
      d='M19 5V19H5V5H19ZM19 3H5C3.9 3 3 3.9 3 5V19C3 20.1 3.9 21 5 21H19C20.1 21 21 20.1 21 19V5C21 3.9 20.1 3 19 3Z'
      fill={colors.NEUTRAL_100}
    />
  </svg>
)

/**
 * Expand/Collapse arrow icon
 */
export const ExpandArrowIcon: React.FC<{ isExpanded: boolean }> = ({
  isExpanded,
}) => (
  <svg
    width='24'
    height='24'
    viewBox='0 0 24 24'
    fill='none'
    xmlns='http://www.w3.org/2000/svg'
    css={css`
      transition: transform 0.2s ease;
      transform: ${isExpanded ? 'rotate(180deg)' : 'rotate(0deg)'};
    `}
  >
    <path
      d='M7.41 8.59L12 13.17L16.59 8.59L18 10L12 16L6 10L7.41 8.59Z'
      fill={colors.NEUTRAL_50}
    />
  </svg>
)

/**
 * Chevron down icon (filled triangle)
 */
export const ChevronDown: React.FC<{ size?: number; color?: string }> = ({
  size = 16,
  color = colors.WHITE,
}) => (
  <svg
    width={size}
    height={size}
    viewBox='0 0 24 24'
    fill='none'
    xmlns='http://www.w3.org/2000/svg'
  >
    <path d='M7 10L12 15L17 10H7Z' fill={color} />
  </svg>
)

/**
 * Chevron right icon (filled triangle)
 */
export const ChevronRight: React.FC<{ size?: number; color?: string }> = ({
  size = 16,
  color = colors.WHITE,
}) => (
  <svg
    width={size}
    height={size}
    viewBox='0 0 24 24'
    fill='none'
    xmlns='http://www.w3.org/2000/svg'
  >
    <path d='M10 7L15 12L10 17V7Z' fill={color} />
  </svg>
)

/**
 * Clock with plus icon - used for "Add Time Slot"
 */
export const ClockPlusIcon: React.FC<{ size?: number; color?: string }> = ({
  size = 18,
  color = 'currentColor',
}) => (
  <svg
    width={size}
    height={size}
    viewBox='0 0 24 24'
    fill='none'
    xmlns='http://www.w3.org/2000/svg'
  >
    {/* Clock circle */}
    <path
      d='M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C12.69 22 13.36 21.93 14 21.8V19.74C13.37 19.91 12.7 20 12 20C7.58 20 4 16.42 4 12C4 7.58 7.58 4 12 4C16.42 4 20 7.58 20 12C20 12.7 19.91 13.37 19.74 14H21.8C21.93 13.36 22 12.69 22 12C22 6.48 17.52 2 12 2Z'
      fill={color}
    />
    {/* Clock hands */}
    <path d='M12.5 7H11V13L16.25 16.15L17 14.92L12.5 12.25V7Z' fill={color} />
    {/* Plus sign */}
    <path d='M19 15V18H16V20H19V23H21V20H24V18H21V15H19Z' fill={color} />
  </svg>
)

/**
 * More vertical icon (3 dots)
 */
export const MoreVertIcon: React.FC<{ size?: number; color?: string }> = ({
  size = 16,
  color = colors.WHITE,
}) => (
  <svg
    width={size}
    height={size}
    viewBox='0 0 24 24'
    fill='none'
    xmlns='http://www.w3.org/2000/svg'
  >
    <path
      d='M12 8c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm0 2c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z'
      fill={color}
    />
  </svg>
)
